import { RouteComponentProps } from 'react-router-dom';
import { find, map, noop, toNumber, values } from 'es-toolkit/compat';

import { QuestionsFormData } from 'forms/QuestionsForm';
import { ReceiptPreVisitQuestionDialog } from 'components/business/receipts/ReceiptPreVisitQuestionDialog/PreVisitQuestionDialog';
import { consumer, core } from '@tock/coretypes';
import { firstNonNilProperty } from 'utils/firstNonNil';
import { getOrderedTicketTypeQuestions } from '@tock/shared/utils/questionUtils';
import { parse } from '@tock/query-string';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

interface ConnectProps extends DefaultConnectProps {
  purchase: core.PastPurchase;
  business?: consumer.IBusinessConfig;
  questions: core.TicketTypeQuestion[] | null;
  requestInProgress: boolean;
  surveyExpiration: number | null;
  introText?: string;
  shouldShowAlert: boolean;
  businessFeatures: string[];
  questionnaireIntroText?: core.TicketTypeDetails['questionnaireIntroText'];
}

interface MatchParams {
  purchaseId: string;
}

type NonConnectProps = RouteComponentProps<MatchParams>;

type Props = ConnectProps & NonConnectProps;

const ReservationSurveyRoute: React.FunctionComponent<Props> = (props) => {
  const handleClose = () => {
    const {
      history,
      match: { params },
      location,
    } = props;
    history.replace({ ...location, pathname: `/profile/reservations/${params.purchaseId}` });
  };

  const handleSubmitQuestionsForm = (
    formData: PartialFields<QuestionsFormData>
  ): Promise<unknown> => {
    const { questions } = formData;
    const { purchaseId } = props.match.params;
    const queryParams = parse(props.location.search);
    const { token } = queryParams;

    if (!questions) {
      return Promise.reject();
    }
    return props.actions.purchase
      .submitSurvey(toNumber(purchaseId), questions, token)
      .then(() => {
        props.actions.toast.showInfo('Thank you! Your answers have been submitted.');
        handleClose();
      })
      .catch(noop);
  };

  const { business, questions, purchase } = props;

  if (!business || !questions) {
    return null;
  }

  return (
    <ReceiptPreVisitQuestionDialog
      business={business}
      isAuthenticated={!!props.app.jwtToken}
      mode="PROFILE"
      purchase={purchase}
      onClose={handleClose}
      questions={questions}
      questionnaireIntroText={props.questionnaireIntroText}
      onSubmitQuestionsForm={handleSubmitQuestionsForm}
    />
  );
};

export default connect<NonConnectProps, ConnectProps>(
  ReservationSurveyRoute,
  (state: StoreType) => {
    const {
      introText,
      questions,
      pastPurchase,
      requestInProgress,
      surveyExpiration,
      ticketTypeDetails,
    } = state.pastPurchase;
    const purchase = values(pastPurchase)?.[0] ?? {};
    const pastPurchaseQuestions = purchase.question ?? [];

    const orderedQuestions = getOrderedTicketTypeQuestions(questions ?? []);
    const formattedQuestions = map(orderedQuestions, (question) => {
      const foundQuestion =
        find(
          pastPurchaseQuestions,
          (answeredQuestion) => answeredQuestion.name === question.name
        ) || {};
      const responseType = firstNonNilProperty(foundQuestion, [
        'textResponse',
        'radioResponse',
        'checkboxResponse',
      ]);

      return {
        ...question,
        response: responseType?.value,
        otherResponse: responseType?.otherValue,
      };
    });
    return {
      purchase,
      questionnaireIntroText: ticketTypeDetails?.questionnaireIntroText,
      introText: introText ?? undefined,
      business: state.business.business ?? undefined,
      questions: formattedQuestions,
      requestInProgress,
      surveyExpiration,
      shouldShowAlert: ticketTypeDetails?.shouldPromptSurvey ?? false,
      businessFeatures: state.business.config?.feature ?? [],
    };
  }
);
